import {
	CurrencyEuroIcon,
	DocumentTextIcon,
	InformationCircleIcon,
	PencilIcon,
	TrashIcon,
} from "@heroicons/react/20/solid";
import { UsersIcon } from "@heroicons/react/24/outline";
import ButtonWithPopoverDots from "components/buttons/ButtonWithPopoverDots";
import Status from "components/statusDisplay/Status";
import Decimal from "decimal.js-light";
import CreateStaffingSidebar from "modules/project/components/ProjectDetailsView/components/CreateStaffingSidebar";
import DeleteOrderModal from "modules/project/components/ProjectDetailsView/components/DeleteOrderModal";
import DeleteStaffingModal from "modules/project/components/ProjectDetailsView/components/DeleteStaffingModal";
import type { FunctionComponent } from "react";
import { useMemo, useState } from "react";

import Button from "~/components/buttons/Button";
import Card from "~/components/Card";
import { SwitchPlain } from "~/components/formElements/Switch/Switch.tsx";
import SectionHeading from "~/components/headings/SectionHeading";
import SectionHeadingTabs from "~/components/headings/SectionHeadingTabbar";
import Headline from "~/components/Headline";
import HorizontalStats from "~/components/HorizontalStats";
import type { HorizontalStat } from "~/components/HorizontalStats/horizontalStatsTypes.ts";
import type { Quota } from "~/modules/client/api/quota/quotaTypes.ts";
import { getCareerLevelDisplayNameById } from "~/modules/humanResources/utils/careerLevelUtils.ts";
import type { OrderWithBillingSettings } from "~/modules/project/api/order/orderTypes.ts";
import type { ProjectRole } from "~/modules/project/api/projectRole/projectRoleTypes.ts";
import type { Staffing } from "~/modules/project/api/staffing/staffingTypes.ts";
import CreateAdditionalStaffingForUserSidebar
	from "~/modules/project/components/ProjectDetailsView/components/CreateAdditionalStaffingForUserSidebar";
import MonthlyTimeTrackingReport
	from "~/modules/project/components/ProjectDetailsView/components/OrderDetails/components/MonthlyTimeTrackingReport";
import OrderBillingSettingsDetails
	from "~/modules/project/components/ProjectDetailsView/components/OrderDetails/components/OrderBillingSettingsDetails.tsx";
import StaffingsTable
	from "~/modules/project/components/ProjectDetailsView/components/OrderDetails/components/StaffingsTable";
import type {
	StaffingsTableData,
} from "~/modules/project/components/ProjectDetailsView/components/OrderDetails/components/StaffingsTable/staffingsTableTypes.ts";
import UpdateOrderBillingSettingsSidebar
	from "~/modules/project/components/ProjectDetailsView/components/OrderDetails/components/UpdateOrderBillingSettingsSidebar/UpdateOrderBillingSettingsSidebar.tsx";
import type {
	OrderDetailsTab,
} from "~/modules/project/components/ProjectDetailsView/components/Orders/ordersViewTypes.ts";
import UpdateOrderSidebar from "~/modules/project/components/ProjectDetailsView/components/UpdateOrderSidebar";
import UpdateStaffingSidebar from "~/modules/project/components/ProjectDetailsView/components/UpdateStaffingSidebar";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";
import { formatDateRange } from "~/utils/dateAndTimeUtils.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

interface OrderProps {
	allUsers: User[];
	clientId: string;
	onOrderDeleteSuccess: (deletedOrderId: string) => void;
	orderData: OrderWithBillingSettings;
	projectRoles: ProjectRole[];
	quota?: Quota;
	setVisibleSection: (section: OrderDetailsTab) => void;
	showBudget: boolean;
	staffings: Staffing[];
	totalOrders: number;
	visibleSection: OrderDetailsTab;
}


const OrderDetails: FunctionComponent<OrderProps> = ({
	allUsers,
	clientId,
	orderData,
	onOrderDeleteSuccess,
	projectRoles,
	quota,
	setVisibleSection,
	showBudget,
	staffings,
	totalOrders,
	visibleSection,
}) => {
	const {
		budgetCents,
		budgetCentsTracked,
		manDays: manDaysPlanned,
		minutesTracked,
		confirmedAt,
		endDate,
		orderNumber,
		projectId,
		startDate,
		title,
	} = orderData;

	const [showCreateStaffingSidebar, setShowCreateStaffingSidebar] = useState(false);
	const [showUpdateBillingSettingsSidebar, setShowUpdateBillingSettingsSidebar] = useState<boolean>(false);
	const [showUpdateOrderSidebar, setShowUpdateOrderSidebar] = useState(false);
	const [showDeleteOrderSidebar, setShowDeleteOrderSidebar] = useState(false);
	const [selectedStaffingIdForUpdate, setSelectedStaffingIdForUpdate] = useState<string | null>(null);
	const [selectedStaffingIdForDeletion, setSelectedStaffingIdForDeletion] = useState<string | null>(null);
	const [showIneffectiveStaffings, setShowIneffectiveStaffings] = useState<boolean>(false);
	const [userIdToCreateStaffingFor, setUserIdToCreateStaffingFor] = useState<string | null>(null);

	const orderDetailsData = useMemo(() => {
		const commonDetails = [
			{ label: "Bezeichnung", content: title },
			{ label: "Bestellnummer", content: orderNumber || "n/a" },
			{ label: "Bestätigt", content: confirmedAt ? "Ja" : "Nein" },
		];
		if (quota) {
			return [...commonDetails,
				{ label: "Budget", content: formatCentsToCurrency(budgetCents, 2) + " (aus Abrechnungskontingent)" },
			];
		}

		const averageDailyRateCents = (parseFloat(manDaysPlanned) > 0 && budgetCents) ? budgetCents / parseFloat(manDaysPlanned) : undefined;

		return [...commonDetails,
			{ label: "Personentage", content: formatNumberWithComma(parseFloat(manDaysPlanned), 2) },
			{ label: "Budget", content: formatCentsToCurrency(budgetCents) },
			{
				label: "Ø Tagessatz",
				content: averageDailyRateCents ? formatCentsToCurrency(averageDailyRateCents) : "--",
			},
			{
				label: "Laufzeit",
				content: startDate && endDate ? `${formatDateRange(new Date(startDate), new Date(endDate))}` : "n/a",
			},

		];
	}, [budgetCents, confirmedAt, endDate, manDaysPlanned, orderNumber, quota, startDate, title]);

	const popOverItems = useMemo(() => {
		const items = [
			{
				label: "Bestellung bearbeiten",
				onClick: () => setShowUpdateOrderSidebar(true),
				icon: PencilIcon,
			},
			{
				label: "Abrechnungsdetails bearbeiten",
				onClick: () => setShowUpdateBillingSettingsSidebar(true),
				icon: CurrencyEuroIcon,
			}];

		if (minutesTracked === 0 && totalOrders > 1) {
			items.push({
				label: "Bestellung löschen",
				onClick: () => setShowDeleteOrderSidebar(true),
				icon: TrashIcon,
			});
		}

		return items;

	}, [minutesTracked, totalOrders]);

	const [staffingsTableData, manDaysAssigned, budgetCentsAssigned, ineffectiveStaffingsCount] = useMemo(() => {
		if (staffings && allUsers && projectRoles) {
			let manDaysAssigned = 0;
			let budgetCentsAssigned = 0;
			let ineffectiveStaffingsCount = 0;
			const tableData: StaffingsTableData[] = [];
			staffings.forEach((staffing) => {
				const user = allUsers.find((u) => staffing.userId === u.id)!;
				const projectRoleDisplayName = projectRoles.find((pr) => staffing.projectRoleId === pr.id)?.displayName;

				const manDaysTracked = Math.ceil(staffing.minutesTracked / 60 / 8 * 100) / 100;
				const budgetCentsPlanned = staffing.manDays * staffing.dailyRateCents;
				const careerLevelId = staffing.careerLevelId || user.careerLevelId;

				const careerLevelDisplayName = careerLevelId ? getCareerLevelDisplayNameById(careerLevelId) : null;
				const startDate = new Date(staffing.startDate);
				const endDate = staffing.endDate ? new Date(staffing.endDate) : null;

				const budgetTrackedPercentage = Math.round(staffing.budgetCentsTracked / budgetCentsPlanned * 100);
				const manDaysTrackedPercentage = Math.round(manDaysTracked / staffing.manDays * 100);
				tableData.push({
					avatarImage: user.avatarImage,
					budgetCentsPlanned,
					budgetCentsTracked: staffing.budgetCentsTracked,
					budgetTrackedPercentage,
					careerLevelDisplayName,
					dailyRateCents: staffing.dailyRateCents,
					endDate,
					id: staffing.id,
					isActive: staffing.isActive,
					isBillable: staffing.isBillable,
					isCurrent: staffing.isCurrent,
					manDaysPlanned: staffing.manDays,
					manDaysTracked,
					manDaysTrackedPercentage,
					roleDisplayName: projectRoleDisplayName,
					startDate,
					userFullName: user.fullName,
					userFirstName: user.firstName,
					userId: user.id,
					userIsActive: user.isActive,
					userLastName: user.lastName,
				});
				if (staffing.isBillable) {
					manDaysAssigned += staffing.manDays;
					budgetCentsAssigned += new Decimal(staffing.manDays).mul(staffing.dailyRateCents).toNumber();
				}

				if (!staffing.isCurrent) {
					ineffectiveStaffingsCount++;
				}

			});


			return [tableData, manDaysAssigned, budgetCentsAssigned, ineffectiveStaffingsCount];
		}
		return [[], 0, 0, 0];
	}, [staffings, allUsers, projectRoles]);

	const [orderBudgetStats, orderManDaysStats] = useMemo(() => {
		const orderBudgetStats: HorizontalStat[] = [
			{
				label: "Bestellvolumen",
				value: formatCentsToCurrency(budgetCents, 0),
			},
			{
				label: "Im Staffing zugewiesen",
				value: formatCentsToCurrency(budgetCentsAssigned, 0),
				isHighlighted: budgetCentsAssigned > budgetCents,
			},
			{
				label: "Verfügt",
				value: formatCentsToCurrency(budgetCentsTracked, 0),
			},
			{
				label: "Rest",
				value: formatCentsToCurrency(budgetCents - budgetCentsTracked, 0),
				isHighlighted: budgetCentsTracked > budgetCents,
			},
		];

		const orderManDaysStats: HorizontalStat[] = [
			{
				label: "Bestellvolumen",
				value: parseFloat(manDaysPlanned).toFixed(0) + " PT",
			},
			{
				label: "Im Staffing zugewiesen",
				value: manDaysAssigned.toFixed(0) + " PT",
				isHighlighted: manDaysAssigned > parseFloat(manDaysPlanned),
			},
			{
				label: "Verfügt",
				value: Math.round(minutesTracked / 60 / 8).toFixed(0) + " PT",
			},
			{
				label: "Rest",
				value: (parseFloat(manDaysPlanned) - Math.round(minutesTracked / 60 / 8)).toFixed(0) + " PT",
				isHighlighted: budgetCentsTracked > budgetCents,
			},
		];


		return [orderBudgetStats, orderManDaysStats];
	}, [budgetCents, budgetCentsAssigned, budgetCentsTracked, manDaysAssigned, manDaysPlanned, minutesTracked]);

	const totalManDaysUnassigned = parseFloat(manDaysPlanned) - manDaysAssigned;
	const totalBudgetUnassigned = budgetCents - budgetCentsAssigned;

	const handleTabClick = (tabIndex: OrderDetailsTab) => {
		setVisibleSection(tabIndex);
	};

	return (
		<>
			<section className="flex flex-col">
				<div className="rounded-t-lg bg-gray-200 p-4 text-gray-500 transition-colors duration-200">
					<div className="flex justify-between gap-8">
						<Headline type="h4"
								  className="mb-0">
							{title}
						</Headline>
						<div className="flex items-center justify-end gap-2">
							<Status theme={null === confirmedAt ? "error" : "success"}>
								{null === confirmedAt ? "Nicht bestätigt" : "Bestätigt"}
							</Status>
							<Status>
								Bestellnummer: {orderNumber || <span className="ml-1 opacity-40">nicht vorhanden</span>}
							</Status>
							<ButtonWithPopoverDots
								theme="dark"
								items={popOverItems}
							/>
						</div>
					</div>
				</div>
				<div className="relative rounded-b-lg bg-gray-400/5">
					<div className="mx-4 mt-4 flex gap-x-4">
						<div className="min-w-0 grow">
							<Card>
								<HorizontalStats className="py-2"
												 stats={orderBudgetStats} />
							</Card>
						</div>
						<div className="min-w-0 grow">
							<Card>
								<HorizontalStats className="py-2"
												 stats={orderManDaysStats} />
							</Card>
						</div>
					</div>
					<div className="px-4">
						<SectionHeading theme={undefined === visibleSection ? "none" : "dark"}>
							<div className="flex w-full items-center justify-between">
								<SectionHeadingTabs
									tabs={[
										{
											name: `Staffing (${staffings.length})`,
											value: "staffings",
											icon: <UsersIcon className="w-5" />,
											active: visibleSection === "staffings",
										},
										{
											name: "Rechnungen",
											value: "monthly-overview",
											icon: <DocumentTextIcon className="w-5" />,
											active: visibleSection === "monthly-overview",
										},
										{
											name: "Bestelldetails",
											value: "details",
											icon: <InformationCircleIcon className="w-5" />,
											active: visibleSection === "details",
										},
										{
											name: "Abrechnungsdetails",
											value: "billing-settings",
											icon: <CurrencyEuroIcon className="w-5" />,
											active: visibleSection === "billing-settings",
										},
									]}
									onTabClick={handleTabClick}
								/>
								{"staffings" === visibleSection && <div className="flex items-center gap-x-4">
									{ineffectiveStaffingsCount > 0 &&
										<SwitchPlain inputSize="xs"
													 label={`Ungültige anzeigen (${ineffectiveStaffingsCount})`}
													 checked={showIneffectiveStaffings}
													 onChange={() => setShowIneffectiveStaffings(!showIneffectiveStaffings)} />}
									<Button size="sm"
											onClick={() => setShowCreateStaffingSidebar(true)}>Neues
										Staffing</Button></div>}
								{"details" === visibleSection && <Button size="sm"
																		 onClick={() => setShowUpdateOrderSidebar(true)}>Bestelldetails
									bearbeiten
								</Button>}
								{"billing-settings" === visibleSection && <Button size="sm"
																				  onClick={() => setShowUpdateBillingSettingsSidebar(true)}>Abrechnungsdetails
									bearbeiten
								</Button>}
							</div>
						</SectionHeading>
					</div>
					<div>
						{"staffings" === visibleSection &&
							<div className="px-4 py-3">
								<StaffingsTable tableData={staffingsTableData}
												showIneffectiveStaffings={showIneffectiveStaffings}
												onDeleteStaffingClick={setSelectedStaffingIdForDeletion}
												onUpdateStaffingClick={setSelectedStaffingIdForUpdate}
												onAddAdditionalStaffingClick={setUserIdToCreateStaffingFor}
												projectId={projectId}
												showBudget={showBudget} />
							</div>}

						{"details" === visibleSection && (
							<dl className="m-4 divide-y divide-gray-100 rounded-lg bg-white px-4 py-2">
								{orderDetailsData.map(({ label, content }, dIndex) => (
									<div key={dIndex}
										 className="flex items-center gap-x-4 py-1">
										<dt className="w-28 text-sm font-medium leading-6 text-gray-900">{label}</dt>
										<dd className="text-sm leading-6 text-gray-700 ">
											{content}
										</dd>
									</div>
								))}
							</dl>
						)}

						{"billing-settings" === visibleSection && (
							<OrderBillingSettingsDetails
								clientId={clientId}
								orderBillingSettings={orderData.billingSettings}
								projectId={projectId}
								quota={quota}
							/>
						)}

						{"monthly-overview" === visibleSection && <MonthlyTimeTrackingReport
							orderId={orderData.id}
							ordersStaffings={staffings}
							projectId={orderData.projectId} />}

					</div>
				</div>
			</section>
			<UpdateOrderSidebar
				isOpen={showUpdateOrderSidebar}
				setOpen={setShowUpdateOrderSidebar}
				projectId={orderData.projectId}
				orderData={orderData}
				quota={quota}
			/>

			<CreateStaffingSidebar
				isOpen={showCreateStaffingSidebar}
				onClose={() => setShowCreateStaffingSidebar(false)}
				orderData={orderData}
				totalBudgetUnassigned={totalBudgetUnassigned}
				totalManDaysUnassigned={totalManDaysUnassigned}
			/>

			<CreateAdditionalStaffingForUserSidebar allStaffings={staffingsTableData}
													isOpen={!!userIdToCreateStaffingFor}
													onClose={() => setUserIdToCreateStaffingFor(null)}
													orderData={orderData}
													totalBudgetUnassigned={totalBudgetUnassigned}
													totalManDaysUnassigned={totalManDaysUnassigned}
													userId={userIdToCreateStaffingFor}
			/>

			<UpdateStaffingSidebar
				isOpen={!!selectedStaffingIdForUpdate}
				close={() => setSelectedStaffingIdForUpdate(null)}
				projectId={orderData.projectId}
				allStaffings={staffingsTableData}
				staffingId={selectedStaffingIdForUpdate}
				totalBudgetUnassigned={totalBudgetUnassigned}
				totalManDaysUnassigned={totalManDaysUnassigned}
			/>

			<DeleteStaffingModal
				isOpen={!!selectedStaffingIdForDeletion}
				staffingId={selectedStaffingIdForDeletion}
				projectId={orderData.projectId}
				onCloseClick={() => setSelectedStaffingIdForDeletion(null)}
			/>
			<DeleteOrderModal
				isOpen={showDeleteOrderSidebar}
				onOrderDeleteSuccess={onOrderDeleteSuccess}
				orderId={orderData.id}
				orderTitle={orderData.title}
				projectId={orderData.projectId}
				onCloseClick={() => setShowDeleteOrderSidebar(false)}
			/>
			<UpdateOrderBillingSettingsSidebar isOpen={showUpdateBillingSettingsSidebar}
											   onClose={() => setShowUpdateBillingSettingsSidebar(false)}
											   clientId={clientId}
											   order={orderData} />
		</>
	);
};

export default OrderDetails;
